import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { remindersApi, type ApiReminder } from '@/api/reminders';

export type ReminderCategory =
  | 'vaccination'
  | 'health-checkup'
  | 'medication'
  | 'grooming'
  | 'follow-up'
  | 'document-renewal'
  | 'custom';

export type ReminderPriority = 'normal' | 'important' | 'critical';

export type ReminderStatus =
  | 'upcoming'
  | 'due-today'
  | 'overdue'
  | 'completed'
  | 'snoozed';

export type RepeatOption =
  | 'one-time'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'custom';

export interface ReminderNotification {
  enabled: boolean;
  channels: ('in-app' | 'email' | 'sms')[];
  timing: ('at-time' | '1-day-before' | '3-days-before' | 'custom')[];
  customDays?: number;
}

export interface Reminder {
  id: string;
  title: string;
  category: ReminderCategory;
  petId?: string;
  animalId?: string;
  clientId?: string;
  date: string;
  time: string;
  repeat: RepeatOption;
  customRepeatDays?: number;
  priority: ReminderPriority;
  status: ReminderStatus;
  notes?: string;
  createdBy: string;
  isInternal?: boolean;
  autoGenerated?: boolean;
  linkedAppointmentId?: string;
  linkedRecordId?: string;
  notifications: ReminderNotification;
  snoozedUntil?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AutoReminderData {
  vaccineName?: string;
  petName?: string;
  animalName?: string;
  reason?: string;
  petId?: string;
  animalId?: string;
  clientId?: string;
  date: string;
  time?: string;
  notes?: string;
  createdBy: string;
  appointmentId?: string;
  recordId?: string;
}

interface RemindersContextType {
  reminders: Reminder[];
  loading: boolean;
  refresh: () => Promise<void>;
  addReminder: (reminder: Omit<Reminder, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateReminder: (id: string, updates: Partial<Reminder>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  markAsCompleted: (id: string) => Promise<void>;
  snoozeReminder: (id: string, until: string) => Promise<void>;
  getRemindersForDate: (date: Date) => Reminder[];
  getOverdueReminders: () => Reminder[];
  getDueTodayReminders: () => Reminder[];
  getUpcomingReminders: (days: number) => Reminder[];
  getRemindersByPet: (petId: string) => Reminder[];
  getRemindersByAnimal: (animalId: string) => Reminder[];
  getRemindersByClient: (clientId: string) => Reminder[];
  generateAutoReminder: (type: 'vaccination' | 'checkup' | 'follow-up', data: AutoReminderData) => Promise<void>;
}

const RemindersContext = createContext<RemindersContextType | undefined>(undefined);

function toReminder(r: ApiReminder): Reminder {
  return { ...r, createdBy: r.userClerkId } as Reminder;
}

export function RemindersProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    try {
      setLoading(true);
      const { items } = await remindersApi.list({ limit: 200 });
      setReminders(items.map(toReminder));
    } catch (err) {
      console.error('Failed to fetch reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (isLoaded && isSignedIn) refresh();
    if (isLoaded && !isSignedIn) setLoading(false);
  }, [refresh, isLoaded, isSignedIn]);

  const addReminder = async (reminder: Omit<Reminder, 'id' | 'createdAt' | 'updatedAt'>) => {
    const created = await remindersApi.create(reminder as any);
    setReminders(prev => [...prev, toReminder(created)]);
  };

  const updateReminder = async (id: string, updates: Partial<Reminder>) => {
    const updated = await remindersApi.update(id, updates as any);
    setReminders(prev => prev.map(r => r.id === id ? toReminder(updated) : r));
  };

  const deleteReminder = async (id: string) => {
    await remindersApi.remove(id);
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const markAsCompleted = async (id: string) => {
    const updated = await remindersApi.complete(id);
    setReminders(prev => prev.map(r => r.id === id ? toReminder(updated) : r));
  };

  const snoozeReminder = async (id: string, until: string) => {
    const updated = await remindersApi.snooze(id, until);
    setReminders(prev => prev.map(r => r.id === id ? toReminder(updated) : r));
  };

  const getRemindersForDate = (date: Date): Reminder[] => {
    const dateStr = date.toISOString().split('T')[0];
    return reminders.filter(r => {
      const rDate = new Date(r.date).toISOString().split('T')[0];
      return rDate === dateStr && r.status !== 'completed';
    });
  };

  const getOverdueReminders = (): Reminder[] => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return reminders.filter(r => {
      const rDate = new Date(r.date); rDate.setHours(0, 0, 0, 0);
      return rDate < today && r.status !== 'completed';
    });
  };

  const getDueTodayReminders = (): Reminder[] => getRemindersForDate(new Date());

  const getUpcomingReminders = (days: number): Reminder[] => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const futureDate = new Date(today); futureDate.setDate(futureDate.getDate() + days);
    return reminders.filter(r => {
      const rDate = new Date(r.date); rDate.setHours(0, 0, 0, 0);
      return rDate >= today && rDate <= futureDate && r.status !== 'completed';
    });
  };

  const getRemindersByPet = (petId: string) => reminders.filter(r => r.petId === petId);
  const getRemindersByAnimal = (animalId: string) => reminders.filter(r => r.animalId === animalId);
  const getRemindersByClient = (clientId: string) => reminders.filter(r => r.clientId === clientId);

  const generateAutoReminder = async (type: 'vaccination' | 'checkup' | 'follow-up', data: AutoReminderData) => {
    let title = ''; let category: ReminderCategory = 'custom'; let priority: ReminderPriority = 'normal';
    switch (type) {
      case 'vaccination': title = `${data.vaccineName} Vaccination Due`; category = 'vaccination'; priority = 'important'; break;
      case 'checkup': title = `Health Checkup - ${data.petName || data.animalName}`; category = 'health-checkup'; priority = 'important'; break;
      case 'follow-up': title = `Follow-up: ${data.reason}`; category = 'follow-up'; priority = 'normal'; break;
    }
    await addReminder({
      title, category, petId: data.petId, animalId: data.animalId, clientId: data.clientId,
      date: data.date, time: data.time || '09:00', repeat: 'one-time', priority, status: 'upcoming',
      notes: data.notes, createdBy: data.createdBy, autoGenerated: true,
      linkedAppointmentId: data.appointmentId, linkedRecordId: data.recordId,
      notifications: { enabled: true, channels: ['in-app', 'email'], timing: ['1-day-before', 'at-time'] },
    });
  };

  return (
    <RemindersContext.Provider
      value={{
        reminders, loading, refresh, addReminder, updateReminder, deleteReminder,
        markAsCompleted, snoozeReminder, getRemindersForDate, getOverdueReminders,
        getDueTodayReminders, getUpcomingReminders, getRemindersByPet,
        getRemindersByAnimal, getRemindersByClient, generateAutoReminder,
      }}
    >
      {children}
    </RemindersContext.Provider>
  );
}

export function useReminders() {
  const context = useContext(RemindersContext);
  if (context === undefined) throw new Error('useReminders must be used within a RemindersProvider');
  return context;
}
